import { useState } from "react";
import { MapPin, Phone, Mail, Clock, X, Facebook, Instagram } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { waLink } from "@/api";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const HouseMark = () => (
  <svg width="46" height="34" viewBox="0 0 92 68" fill="none" className="shrink-0">
    <path d="M4 64 H28 V34 L46 16 L64 34 V64 H88" stroke="#F5F0E8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="40" y="38" width="12" height="12" stroke="#C67A5C" strokeWidth="2.5" />
    <path d="M46 38 V50 M40 44 H52" stroke="#C67A5C" strokeWidth="2" />
  </svg>
);

export default function Footer({ config }) {
  const { t } = useLang();
  const [legal, setLegal] = useState(null);
  const wa = config?.whatsapp || "40721663181";

  const links = [
    ["#camere", t("nav.rooms")],
    ["#casa", t("nav.house")],
    ["#experiente", t("nav.experiences")],
    ["#atractii", t("nav.attractions")],
    ["#recenzii", t("nav.reviews")],
    ["#rezervare", t("nav.book")],
  ];

  return (
    <footer className="bg-main text-cream pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <HouseMark />
              <div>
                <p className="font-serif text-3xl tracking-wide">IMPERFECT</p>
                <p className="font-serif italic text-lg text-sage -mt-1">house</p>
              </div>
            </div>
            <p className="mt-6 font-serif italic text-xl text-cream/70 max-w-sm">„{t("footer.tagline")}"</p>
            <p className="mt-8 text-sm text-cream/50">{t("footer.final")}</p>
          </div>

          <div className="md:col-span-4">
            <h3 className="text-xs tracking-[0.25em] uppercase font-bold text-sage">{t("footer.contact")}</h3>
            <ul className="mt-5 space-y-3.5 text-sm text-cream/80">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-terracotta shrink-0" />
                {config?.address || "Strada Ion Luca Caragiale nr. 127, Râșnov"}
              </li>
              <li>
                <a href={`tel:+${wa}`} className="flex items-center gap-3 hover:text-terracotta transition-colors" data-testid="footer-phone">
                  <Phone size={16} className="text-terracotta shrink-0" /> {config?.phone_display || "+40 721 663 181"}
                </a>
              </li>
              <li>
                <a href={`mailto:${config?.email || "contact@imperfecthouse.ro"}`} className="flex items-center gap-3 hover:text-terracotta transition-colors" data-testid="footer-email">
                  <Mail size={16} className="text-terracotta shrink-0" /> {config?.email || "contact@imperfecthouse.ro"}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={16} className="mt-0.5 text-terracotta shrink-0" />
                <span>{t("footer.checkin")} {config?.checkin || "15:00 – 22:00"} · {t("footer.checkout")} {config?.checkout || "11:00"}</span>
              </li>
            </ul>
            <a href={waLink(wa, "Bună, Mihai!")} target="_blank" rel="noreferrer" data-testid="footer-whatsapp"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-cream/25 px-5 py-2.5 text-sm font-semibold hover:border-terracotta hover:text-terracotta transition-all duration-300">
              WhatsApp
            </a>
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
              {[
                ["social_facebook", <Facebook key="fb" size={18} />, "Facebook", "footer-facebook"],
                ["social_instagram", <Instagram key="ig" size={18} />, "Instagram", "footer-instagram"],
                ["social_tiktok", <TikTokIcon key="tt" />, "TikTok", "footer-tiktok"],
              ].map(([key, icon, label, testid]) => (
                <a
                  key={key}
                  href={config?.[key] || "#"}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={testid}
                  className="w-10 h-10 rounded-full border border-cream/25 flex items-center justify-center text-cream/80 hover:border-terracotta hover:text-terracotta transition-all duration-300"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h3 className="text-xs tracking-[0.25em] uppercase font-bold text-sage">{t("footer.explore")}</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {links.map(([id, label]) => (
                <li key={id}>
                  <button onClick={() => scrollToId(id)} className="text-cream/80 hover:text-terracotta transition-colors" data-testid={`footer-link-${id.slice(1)}`}>
                    {label}
                  </button>
                </li>
              ))}
            </ul>
            <h3 className="mt-8 text-xs tracking-[0.25em] uppercase font-bold text-sage">{t("footer.legal")}</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><button onClick={() => setLegal("privacy")} className="text-cream/70 hover:text-terracotta transition-colors" data-testid="footer-privacy">{t("footer.privacy")}</button></li>
              <li><button onClick={() => setLegal("terms")} className="text-cream/70 hover:text-terracotta transition-colors" data-testid="footer-terms">{t("footer.terms")}</button></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-5" data-testid="legal-banners">
          <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noreferrer" data-testid="banner-anpc-sal" className="hover:opacity-80 transition-opacity">
            <img src="/assets/anpc-sal.webp" alt="ANPC — Soluționarea Alternativă a Litigiilor" className="h-10 w-auto" />
          </a>
          <a href="https://ec.europa.eu/consumers/odr/main/index.cfm?event=main.home2.show&lng=RO" target="_blank" rel="noreferrer" data-testid="banner-anpc-sol" className="hover:opacity-80 transition-opacity">
            <img src="/assets/anpc-sol.webp" alt="Soluționarea Online a Litigiilor" className="h-10 w-auto" />
          </a>
        </div>

        <div className="mt-10 pt-8 border-t border-cream/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-cream/40">
          <p>© {new Date().getFullYear()} Imperfect House · Râșnov. {t("footer.rights")}</p>
          <a href="/admin" className="hover:text-terracotta transition-colors" data-testid="footer-admin-link">Admin</a>
        </div>
      </div>

      {legal && (
        <div className="fixed inset-0 z-[80] bg-main/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setLegal(null)} data-testid="legal-modal">
          <div className="bg-cream-light text-main max-w-lg w-full rounded-3xl p-8 md:p-10 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLegal(null)} className="absolute top-5 right-5 text-brown/50 hover:text-terracotta" data-testid="legal-close">
              <X size={20} />
            </button>
            <h3 className="font-serif text-3xl">{t(`legal.${legal}_title`)}</h3>
            <p className="mt-5 text-sm text-brown/80 font-light leading-relaxed">{t(`legal.${legal}_body`)}</p>
          </div>
        </div>
      )}
    </footer>
  );
}
